import { useMemo, useState } from "react";
import { type SortDescriptor, TableBody } from "react-aria-components";

import { Link } from "@/components/link";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { Switch } from "@/components/switch";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";
import { toast } from "@/components/toast/toast-queue";
import { NativeTooltip } from "@/components/tooltip";
import { FeaturePageLayout } from "@/features/shared/layout";

import { categoryLabel } from "../category-labels";
import { useMbcCatalog } from "./hooks/useMbcCatalog";
import { useSetDesignTypeMbc } from "./hooks/useSetDesignTypeMbc";

const ALL_CATEGORIES = "all";

const CATEGORY_FILTER_OPTIONS = [
  { id: ALL_CATEGORIES, label: "All categories" },
  { id: "graphic", label: "Graphic" },
  { id: "video", label: "Video" },
  { id: "webui", label: "Web & UI" },
];

const MBC_BLOCKED_REASON = "MBC is not available for video design types";

const isMbcBlocked = ({
  category,
  isMbcCompatible,
}: {
  category: null | string;
  isMbcCompatible: boolean;
}) => category === "video" && !isMbcCompatible;

export function DesignTypesPage() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>();
  const { data: catalog, error, isLoading } = useMbcCatalog();
  const setDesignTypeMbc = useSetDesignTypeMbc();

  const designTypes = useMemo(() => {
    const all = [
      ...(catalog?.subCategories ?? []).flatMap((subCategory) =>
        subCategory.designTypes.map((designType) => ({
          ...designType,
          subCategoryName: subCategory.name,
        })),
      ),
      ...(catalog?.uncategorizedDesignTypes ?? []).map((designType) => ({
        ...designType,
        subCategoryName: null,
      })),
    ].filter(
      ({ category, name }) =>
        name.toLowerCase().includes(search.trim().toLowerCase()) &&
        (categoryFilter === ALL_CATEGORIES || category === categoryFilter),
    );

    if (!sortDescriptor) return all;

    const direction = sortDescriptor.direction === "descending" ? -1 : 1;

    return all.sort((a, b) => {
      switch (sortDescriptor.column) {
        case "id": {
          return (a.id - b.id) * direction;
        }
        case "mbc": {
          return (
            (Number(a.isMbcCompatible) - Number(b.isMbcCompatible)) * direction
          );
        }
        case "name": {
          return a.name.localeCompare(b.name) * direction;
        }
        default: {
          return 0;
        }
      }
    });
  }, [catalog, search, categoryFilter, sortDescriptor]);

  return (
    <div className="p-xl">
      <FeaturePageLayout
        isLoading={isLoading}
        searchBar={{ inputValue: search, setInputValue: setSearch }}
        title="Design types"
      >
        <div className="rounded-b-xl bg-bg-primary">
          {error && (
            <p className="px-xl pb-4 text-sm text-text-error" role="alert">
              Could not load the design types: {error.message}
            </p>
          )}
          <div className="px-xl pb-4">
            <Select
              aria-label="Filter by category"
              className="w-60"
              onSelectionChange={(key) => setCategoryFilter(String(key))}
              selectedKey={categoryFilter}
            >
              <SelectButton />
              <SelectPopover>
                <SelectListBox>
                  {CATEGORY_FILTER_OPTIONS.map(({ id, label }) => (
                    <SelectListItem id={id} key={id}>
                      {label}
                    </SelectListItem>
                  ))}
                </SelectListBox>
              </SelectPopover>
            </Select>
          </div>
          <Table
            aria-label="Design types"
            onSortChange={setSortDescriptor}
            sortDescriptor={sortDescriptor}
          >
            <TableHeader>
              <Column allowsSorting defaultWidth={80} id="id" minWidth={80}>
                ID
              </Column>
              <Column
                allowsSorting
                defaultWidth="3fr"
                id="name"
                isRowHeader
                minWidth={200}
              >
                Name
              </Column>
              <Column defaultWidth="2fr" id="subCategory" minWidth={160}>
                Subcategory
              </Column>
              <Column defaultWidth="1fr" id="category" minWidth={120}>
                Category
              </Column>
              <Column allowsSorting defaultWidth="1fr" id="mbc" minWidth={140}>
                MBC enabled
              </Column>
            </TableHeader>
            <TableBody
              items={designTypes}
              renderEmptyState={() => (
                <p className="p-3xl text-sm text-text-tertiary">
                  No design types found.
                </p>
              )}
            >
              {(designType) => (
                <Row id={designType.id}>
                  <Cell>
                    <span className="whitespace-nowrap font-mono text-text-tertiary">
                      {designType.id}
                    </span>
                  </Cell>
                  <Cell>
                    <Link
                      className="font-medium text-text-primary"
                      href={`/admin/design-types/${designType.id}`}
                    >
                      {designType.name}
                    </Link>
                  </Cell>
                  <Cell>
                    {designType.subCategoryId === null ? (
                      "—"
                    ) : (
                      <Link
                        className="text-sm"
                        href={`/admin/subcategories/${designType.subCategoryId}`}
                      >
                        {designType.subCategoryName}
                      </Link>
                    )}
                  </Cell>
                  <Cell>{categoryLabel(designType.category)}</Cell>
                  <Cell>
                    <NativeTooltip
                      className="w-fit"
                      title={isMbcBlocked(designType) ? MBC_BLOCKED_REASON : ""}
                    >
                      <Switch
                        aria-label={`Toggle MBC for ${designType.name}`}
                        isDisabled={
                          setDesignTypeMbc.isPending || isMbcBlocked(designType)
                        }
                        isSelected={designType.isMbcCompatible}
                        onChange={(enabled) =>
                          setDesignTypeMbc.mutate(
                            { designTypeId: designType.id, enabled },
                            {
                              onError: (mutationError) =>
                                toast.add({
                                  description: mutationError.message,
                                  title: "Could not update the design type",
                                  type: "error",
                                }),
                            },
                          )
                        }
                      />
                    </NativeTooltip>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </div>
      </FeaturePageLayout>
    </div>
  );
}
